import { useEffect, useMemo, useState } from "react";
import { Search, Download, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

function fmtNum(n) {
  if (n == null) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}
function fmtUsd(n) {
  if (n == null) return "—";
  if (n < 0.01) return "$" + n.toFixed(4);
  return "$" + n.toFixed(2);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}
function fmtRel(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * AdminTokenUsageSection — full cross-user roll-up with filters,
 * pagination, power-user flagging, and CSV export. Renders inside
 * the /admin dashboard.
 */
export default function AdminTokenUsageSection() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [threshold, setThreshold] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // Filters
  const [days, setDays] = useState(30);
  const [engine, setEngine] = useState("");
  const [tier, setTier] = useState("");
  const [minTokens, setMinTokens] = useState(0);
  const [sort, setSort] = useState("tokens_desc");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  // Debounce the email query so we don't hammer the API on every keystroke.
  const debouncedQ = useDebounced(q, 300);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/admin/usage/tokens/summary`, { credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) console.warn("summary failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      days: String(days),
      sort,
      skip: String(page * PAGE_SIZE),
      limit: String(PAGE_SIZE),
    });
    if (engine) params.set("engine", engine);
    if (tier) params.set("tier", tier);
    if (minTokens) params.set("min_tokens", String(minTokens));
    if (debouncedQ) params.set("q", debouncedQ);
    (async () => {
      try {
        const r = await fetch(`${API}/admin/usage/tokens/users?${params.toString()}`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setThreshold(data.power_user_threshold || 0);
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Failed to load users");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, engine, tier, minTokens, sort, debouncedQ, page]);

  async function handleExport() {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/admin/usage/tokens/export?days=${days}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hbm-token-usage-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setDownloading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5" data-testid="admin-token-usage">
      {/* System-wide summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryTile label="30d tokens" value={fmtNum(summary.totals?.tokens_30d || 0)} testid="sum-30d" />
          <SummaryTile label="30d chats" value={fmtNum(summary.totals?.calls_30d || 0)} testid="sum-calls" />
          <SummaryTile label="30d cost" value={fmtUsd(summary.totals?.cost_30d)} testid="sum-cost" />
          <SummaryTile label="All-time tokens" value={fmtNum(summary.totals?.tokens_all || 0)} testid="sum-alltime" />
          <SummaryTile label="Power-user cutoff" value={fmtNum(threshold)} sublabel="top 10% (tokens)" testid="sum-p90" />
        </div>
      )}

      {/* Filters */}
      <div className="border border-[var(--border)] p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">Window</span>
            <select
              value={days}
              onChange={(e) => { setDays(Number(e.target.value)); setPage(0); }}
              data-testid="filter-days"
              className="bg-transparent border border-[var(--border)] px-2 py-1.5 font-mono-ui text-sm text-[var(--text)] focus:border-[var(--accent)] outline-none"
            >
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">Engine</span>
            <select
              value={engine}
              onChange={(e) => { setEngine(e.target.value); setPage(0); }}
              data-testid="filter-engine"
              className="bg-transparent border border-[var(--border)] px-2 py-1.5 font-mono-ui text-sm text-[var(--text)] focus:border-[var(--accent)] outline-none"
            >
              <option value="">All</option>
              <option value="nt">NT Think</option>
              <option value="nvidia_ultra">Nvidia Ultra</option>
              <option value="nvidia_nemotron">Nvidia Nemotron</option>
              <option value="inkling">Inkling</option>
              <option value="coding_agent">Coding Agent</option>
              <option value="ox_alpha">Ox Alpha</option>
              <option value="avian_deepseek">Avian DeepSeek</option>
              <option value="deepseek">DeepSeek</option>
              <option value="nscale_qwen">Nscale Qwen</option>
              <option value="sarvam">Sarvam</option>
              <option value="gemini">Gemini</option>
              <option value="openai">OpenAI</option>
              <option value="openrouter_free">OpenRouter Free Router</option>
              <option value="glm_free">GLM Free</option>
              <option value="claude">Claude</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">Tier</span>
            <select
              value={tier}
              onChange={(e) => { setTier(e.target.value); setPage(0); }}
              data-testid="filter-tier"
              className="bg-transparent border border-[var(--border)] px-2 py-1.5 font-mono-ui text-sm text-[var(--text)] focus:border-[var(--accent)] outline-none"
            >
              <option value="">All</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="plus">Plus</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">Min tokens</span>
            <input
              type="number"
              value={minTokens}
              onChange={(e) => { setMinTokens(Number(e.target.value) || 0); setPage(0); }}
              data-testid="filter-min-tokens"
              className="bg-transparent border border-[var(--border)] px-2 py-1.5 font-mono-ui text-sm text-[var(--text)] focus:border-[var(--accent)] outline-none"
              min={0}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">Sort</span>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(0); }}
              data-testid="filter-sort"
              className="bg-transparent border border-[var(--border)] px-2 py-1.5 font-mono-ui text-sm text-[var(--text)] focus:border-[var(--accent)] outline-none"
            >
              <option value="tokens_desc">Tokens (high → low)</option>
              <option value="tokens_asc">Tokens (low → high)</option>
              <option value="recent">Most recent activity</option>
              <option value="cost_desc">Cost (high → low)</option>
              <option value="calls_desc">Chats (high → low)</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">Search email</span>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-dim)]" />
              <input
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="user@example.com"
                data-testid="filter-email"
                className="w-full pl-7 pr-2 py-1.5 bg-transparent border border-[var(--border)] font-mono-ui text-sm text-[var(--text)] placeholder:text-[var(--text-dim)] focus:border-[var(--accent)] outline-none"
              />
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 mt-4">
          <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]" data-testid="results-count">
            {loading ? "Loading…" : `${total.toLocaleString()} user${total === 1 ? "" : "s"} match`}
          </div>
          <button
            onClick={handleExport}
            disabled={downloading || rows.length === 0}
            data-testid="export-csv"
            className="px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.24em] uppercase transition-colors inline-flex items-center gap-2 disabled:opacity-40"
          >
            {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[var(--border)] overflow-x-auto" data-testid="users-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-left">
              <Th>Email</Th>
              <Th className="text-right">Tokens</Th>
              <Th className="text-right hidden md:table-cell">Chats</Th>
              <Th className="text-right hidden md:table-cell">Avg/chat</Th>
              <Th className="hidden lg:table-cell">Engines</Th>
              <Th className="text-right">Cost</Th>
              <Th className="hidden md:table-cell">Last active</Th>
              <Th>Tier</Th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
                  No users match these filters.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.user_id} className="border-b border-[var(--border)] last:border-b-0" data-testid={`user-row-${r.user_id}`}>
                  <Td>
                    <div className="flex items-center gap-2">
                      {r.is_power_user && (
                        <span
                          className="inline-flex items-center gap-1 font-mono-ui text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border border-[var(--accent)] text-[var(--accent)]"
                          title="Power user (top 10% by tokens)"
                          data-testid={`power-badge-${r.user_id}`}
                        >
                          <TrendingUp size={9} /> Power
                        </span>
                      )}
                      <span className="font-ui truncate max-w-[240px]" title={r.email}>
                        {r.email || <span className="text-[var(--text-dim)]">(anon)</span>}
                      </span>
                    </div>
                  </Td>
                  <Td className="text-right font-mono-ui" testid={`row-tokens-${r.user_id}`}>{fmtNum(r.tokens)}</Td>
                  <Td className="text-right font-mono-ui hidden md:table-cell">{r.calls || 0}</Td>
                  <Td className="text-right font-mono-ui hidden md:table-cell">{fmtNum(r.avg_tokens_per_call)}</Td>
                  <Td className="hidden lg:table-cell">
                    <span className="font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[var(--text-dim)]">
                      {(r.engines || []).join(" · ")}
                    </span>
                  </Td>
                  <Td className="text-right font-mono-ui text-[var(--text-muted)]">{fmtUsd(r.cost_usd)}</Td>
                  <Td className="hidden md:table-cell font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[var(--text-dim)]" title={r.last_active}>
                    {fmtRel(r.last_active)}
                  </Td>
                  <Td className="font-mono-ui text-[10px] tracking-[0.24em] uppercase">{r.tier || "free"}</Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            data-testid="page-prev"
            className="px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-40"
          >
            ← Prev
          </button>
          <span data-testid="page-indicator">Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
            disabled={page + 1 >= totalPages}
            data-testid="page-next"
            className="px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)] ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "", testid }) {
  return (
    <td className={`px-4 py-3 text-[var(--text)] ${className}`} data-testid={testid}>
      {children}
    </td>
  );
}
function SummaryTile({ label, value, sublabel, testid }) {
  return (
    <div className="border border-[var(--border)] px-4 py-3" data-testid={testid}>
      <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-mono-ui text-lg text-[var(--text)] mt-1">{value}</div>
      {sublabel && (
        <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1">
          {sublabel}
        </div>
      )}
    </div>
  );
}

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
